import { LinkProvider, type UiLinkProps } from '@fruuit/ui'
import { Link, Navigate, Outlet, type RouteObject } from 'react-router'
import { adminRoutes } from './areas/admin/routes'
import { appRoutes } from './areas/app/routes'
import { HelpHomePage, HelpSectionPage } from './areas/help/Help'
import { workspaceRoutes } from './areas/workspace/routes'
import { AccountPage } from './auth/AccountPage'
import { InvitePage } from './auth/InvitePage'
import { AreaHomeRedirect, AreaRoute, RequireSession } from './auth/RequireSession'
import { SignInCodePage } from './auth/SignInCodePage'
import { SignInPage } from './auth/SignInPage'
import { NoAccessPage, NotFoundPage } from './auth/StatusPages'
import { useSession } from './auth/session-context'
import { AppShell } from './shell/AppShell'
import { currentArea } from './shell/lastArea'

/** `@fruuit/ui` links (breadcrumbs, stat tiles, menus) render through react-router. */
function RouterLink({ href, ...rest }: UiLinkProps) {
  return <Link to={href} {...rest} />
}

function Root() {
  return (
    <LinkProvider component={RouterLink}>
      <Outlet />
    </LinkProvider>
  )
}

/** `/account` and `/help` sit in the shell of the area the user came from. */
function CurrentAreaRoute() {
  const me = useSession()
  const area = currentArea(me)
  if (!area) return <Navigate to="/no-access" replace />
  return <AppShell area={area} />
}

/**
 * The route tree. Areas own their child routes in `src/areas/<area>/routes.tsx`, so lanes
 * add screens without touching this file.
 */
export const routes: RouteObject[] = [
  {
    element: <Root />,
    children: [
      { path: 'signin', element: <SignInPage /> },
      { path: 'signin/code', element: <SignInCodePage /> },
      { path: 'invite/:token', element: <InvitePage /> },
      { path: 'no-access', element: <NoAccessPage /> },
      {
        element: <RequireSession />,
        children: [
          { index: true, element: <AreaHomeRedirect /> },
          { path: 'app', element: <AreaRoute area="app" />, children: appRoutes },
          { path: 'workspace', element: <AreaRoute area="workspace" />, children: workspaceRoutes },
          { path: 'admin', element: <AreaRoute area="admin" />, children: adminRoutes },
          {
            path: 'account',
            element: <CurrentAreaRoute />,
            children: [{ index: true, element: <AccountPage /> }],
          },
          {
            path: 'help',
            element: <CurrentAreaRoute />,
            children: [
              { index: true, element: <HelpHomePage /> },
              { path: ':section', element: <HelpSectionPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]
